import React, { useState } from "react";
import { signInWithEmailAndPassword, GoogleAuthProvider, signInWithPopup } from "firebase/auth";
import { auth, db } from "./firebase";
import { useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";
import { ref, get } from "firebase/database";
import { useAuth } from "./AuthContext";
import Navbar from "./Navbar";
import Footer from "./Footer";
import { FaLock, FaEnvelope } from "react-icons/fa";
import CircularText from "./CircularText";
import DarkVeil from './components/DarkVeil';

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { setRole, setEnrolledCourse } = useAuth();

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Helper function to normalize email
  const normalizeEmail = (email) => {
    return email.trim().toLowerCase().replace(/\./g, ',');
  };

  // Reusable function to handle post-authentication logic
  const handlePostAuthentication = async (user) => {
    // Check if user exists in any course's students list
    const coursesRef = ref(db, "courses");
    const coursesSnapshot = await get(coursesRef);
    if (coursesSnapshot.exists()) {
      const courses = coursesSnapshot.val();
      for (const [courseId, courseData] of Object.entries(courses)) {
        if (courseData.students) {
          const student = Object.values(courseData.students).find(
            (s) => s.email === user.email
          );
          if (student) {
            setRole("student");
            setEnrolledCourse(courseId);
            navigate("/dashboard");
            return;
          }
        }
      }
    }

    // Check if user has a pending registration
    const emailKey = normalizeEmail(user.email);
    const studentRef = ref(db, `students/${emailKey}`);
    const studentSnap = await get(studentRef);

    setRole("student");

    if (studentSnap.exists()) {
      const studentData = studentSnap.val();
      if (studentData.status === "pending" || !studentData.course) {
        navigate("/complete-registration", { replace: true });
      } else {
        navigate("/dashboard");
      }
    } else {
      navigate("/complete-registration", { replace: true });
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    // Validate email format
    if (!validateEmail(email)) {
      setError("Please enter a valid email address.");
      setLoading(false);
      return;
    }

    try {
      console.log("Attempting to sign in with email:", email);
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      console.log("Firebase auth successful:", userCredential.user.uid);

      await handlePostAuthentication(userCredential.user);
    } catch (err) {
      console.error("Login error details:", {
        code: err.code,
        message: err.message,
        fullError: err
      });

      switch (err.code) {
        case "auth/invalid-email":
          setError("Invalid email address format.");
          break;
        case "auth/user-disabled":
          setError("Your account has been disabled. Please contact support.");
          break;
        case "auth/user-not-found":
          setError("No account found with this email address.");
          break;
        case "auth/wrong-password":
          setError("Incorrect password. Please try again.");
          break;
        case "auth/too-many-requests":
          setError("Too many failed attempts. Please try again later or reset your password.");
          break;
        case "auth/network-request-failed":
          setError("Network error. Please check your internet connection.");
          break;
        default:
          setError(`Login failed: ${err.message}`);
          break;
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setLoading(true);
    setError("");

    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      await handlePostAuthentication(result.user);
    } catch (err) {
      // Don't show error if user just closed the popup
      if (err.code === "auth/popup-closed-by-user" || err.code === "auth/cancelled-popup-request") {
        setLoading(false);
        return;
      }

      switch (err.code) {
        case "auth/popup-blocked":
          setError("Please allow popups for this site to sign in with Google.");
          break;
        case "auth/account-exists-with-different-credential":
          setError("This email is already registered. Please use your original sign-in method.");
          break;
        case "auth/user-disabled":
          setError("Your account has been disabled. Contact support.");
          break;
        default:
          setError("Unable to sign in with Google. Please try again.");
      }
      setLoading(false);
    }
  };

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);
    if (value && !validateEmail(value)) {
      setError("Please enter a valid email address.");
    } else {
      setError("");
    }
  };

  return (
    <div className="font-space bg-white text-black min-h-screen relative">
      {/* DarkVeil Background */}
      <div style={{ width: '100%', height: '100vh', position: 'fixed', top: 0, left: 0, zIndex: 0, pointerEvents: 'none' }}>
        <DarkVeil />
      </div>

      <div className="relative z-10">
        <Navbar />

        <section className="relative overflow-hidden py-12 sm:py-16 md:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 flex flex-col lg:flex-row items-center justify-center gap-12">

            {/* Form Section */}
            <div className="w-full lg:w-1/2 max-w-md">
              <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-6 sm:p-8 shadow-2xl">
                <div class="w-full flex justify-center">
                  <div className="p-2 w-28 h-28 sm:w-24 sm:h-24 md:w-28 md:h-28 bg-gradient-to-r from-teal-500 to-sky-600 rounded-full flex items-center justify-center mb-4 sm:mb-6">
                    <div class="scale-50 ">
                      <div className="bg-[url('/Xmaths.png')] bg-center bg-[length:120px] bg-no-repeat filter brightness-0 invert">
                        <CircularText
                          text="✦ SHEMIES ✦ MATH ✦ IGCSE "
                          onHover="speedUp"
                          spinDuration={20}
                          className=""
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <h2 className="text-2xl sm:text-3xl font-bold text-center mb-6 sm:mb-8 text-white drop-shadow-[0_2px_8px_rgba(0,0,0,0.8)]">
                  Welcome Back
                </h2>

                {error && (
                  <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-red-500/20 backdrop-blur-sm text-red-200 rounded-lg border border-red-500/30 text-xs sm:text-sm text-center">
                    {error}
                  </div>
                )}

                <form onSubmit={handleLogin} className="space-y-4 sm:space-y-6">
                  <div className="relative">
                    <label className="block text-sm font-medium text-white/90 mb-2 drop-shadow-[0_1px_2px_rgba(0,0,0,0.5)]">Email</label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <FaEnvelope className="text-white/70" />
                      </div>
                      <input
                        type="email"
                        value={email}
                        onChange={handleEmailChange}
                        onBlur={() => {
                          if (email && !validateEmail(email)) {
                            setError("Please enter a valid email address.");
                          }
                        }}
                        placeholder="Enter your email"
                        className="w-full pl-10 pr-4 py-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg focus:ring-2 focus:ring-teal-500/50 focus:border-teal-500/50 outline-none transition text-white placeholder-white/50"
                        required
                      />
                    </div>
                  </div>

                  <div className="relative">
                    <label className="block text-sm font-medium text-white/90 mb-2 drop-shadow-[0_1px_2px_rgba(0,0,0,0.5)]">Password</label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <FaLock className="text-white/70" />
                      </div>
                      <input
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Enter your password"
                        className="w-full pl-10 pr-4 py-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg focus:ring-2 focus:ring-teal-500/50 focus:border-teal-500/50 outline-none transition text-white placeholder-white/50"
                        required
                      />
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full px-6 sm:px-8 py-3 sm:py-3.5 rounded-full bg-gradient-to-r from-teal-600 to-sky-600 hover:from-teal-500 hover:to-sky-500 text-white text-sm sm:text-base font-medium transition-all duration-300 shadow-lg hover:shadow-teal-500/30 flex justify-center items-center touch-manipulation"
                  >
                    {loading ? (
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    ) : null}
                    {loading ? "Authenticating..." : "Login"}
                  </button>
                </form>

                {/* Divider */}
                <div className="relative my-4 sm:my-6">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-white/20"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-4 bg-white/5 text-white/70 backdrop-blur-sm rounded-full">OR</span>
                  </div>
                </div>

                {/* Google Sign-in Button */}
                <button
                  type="button"
                  onClick={handleGoogleLogin}
                  disabled={loading}
                  className="w-full px-4 sm:px-6 py-3 sm:py-3.5 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 hover:bg-white/20 text-white text-sm sm:text-base font-semibold transition-all duration-200 shadow-lg hover:shadow-xl active:scale-[0.98] flex justify-center items-center gap-2 sm:gap-3 disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation"
                >
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                  </svg>
                  <span className="truncate">{loading ? "Signing in..." : "Continue with Google"}</span>
                </button>

                <div className="mt-4 sm:mt-6 text-center text-xs sm:text-sm text-white/80">
                  <p>
                    Don't have an account?{" "}
                    <Link
                      to="/signup"
                      className="text-teal-300 font-medium hover:text-teal-200 hover:underline transition drop-shadow-[0_1px_2px_rgba(0,0,0,0.5)]"
                    >
                      Create account
                    </Link>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </div>
  );
};

export default Login;